import { useState, useRef, useEffect } from "react";
import { useTheme, useMediaQuery } from "@mui/material";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, Grid, CircularProgress, IconButton, Skeleton,
} from "@mui/material";
import { Download, ChevronLeft, ChevronRight } from "lucide-react";
import api from "../../services/api";

const TEMPLATES = [
  { id: 1, label: "Modern Minimal",    desc: "Centered header, colored accents" },
  { id: 2, label: "Professional",      desc: "Left-aligned, bold section dividers" },
  { id: 3, label: "Creative Clean",    desc: "Colored header bar, pill skill tags" },
  { id: 4, label: "Technical ATS",     desc: "Monospace, maximum ATS readability" },
  { id: 5, label: "Executive Elegant", desc: "Serif font, wide letter-spacing" },
  { id: 6, label: "Sidebar Layout",    desc: "Two-column, colored sidebar" },
];

const COLORS = [
  { id: "navy",     hex: "#1d4ed8", label: "Navy Blue"    },
  { id: "emerald",  hex: "#059669", label: "Emerald"      },
  { id: "charcoal", hex: "#374151", label: "Charcoal"     },
  { id: "purple",   hex: "#7c3aed", label: "Purple"       },
  { id: "crimson",  hex: "#dc2626", label: "Crimson"      },
  { id: "neutral",  hex: "#64748b", label: "Neutral Grey" },
];

const IFRAME_W = 794;
const IFRAME_H = 1123;

export default function DownloadOptionsModal({ open, onClose, onDownload, loading }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [templateIdx, setTemplateIdx] = useState(0);
  const [color, setColor] = useState("navy");
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(520);

  const template = TEMPLATES[templateIdx];
  const palette = COLORS.find((c) => c.id === color) || COLORS[0];

  // Scale iframe to fit the measured container width exactly
  const scale = containerWidth / IFRAME_W;
  const containerHeight = Math.min(isMobile ? 380 : 490, Math.round(IFRAME_H * scale));

  // Re-measure when dialog opens or orientation changes
  useEffect(() => {
    if (!open) return;
    const measure = () => {
      if (containerRef.current) {
        const w = containerRef.current.offsetWidth;
        if (w > 0) setContainerWidth(w);
      }
    };
    const t = setTimeout(measure, 80);
    return () => clearTimeout(t);
  }, [open, isMobile]);

  // Fetch the real template HTML — same HTML used for PDF generation (WYSIWYG)
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPreviewLoading(true);
    api
      .get(`/resume/preview-template?template=${template.id}&color=${color}`, {
        responseType: "text",
      })
      .then((res) => {
        if (!cancelled) {
          setPreviewHtml(res.data);
          setPreviewLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, template.id, color]);

  const prev = () => setTemplateIdx((i) => (i - 1 + TEMPLATES.length) % TEMPLATES.length);
  const next = () => setTemplateIdx((i) => (i + 1) % TEMPLATES.length);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : "16px",
          maxHeight: isMobile ? "100%" : "92vh",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: { xs: 16, sm: 20 } }}>
          Choose Template &amp; Color
        </Typography>
        <Typography variant="body2" sx={{ color: "#64748b", mt: 0.25, fontSize: { xs: 12, sm: 14 } }}>
          Preview shows the exact style your PDF will use.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, overflow: "auto" }}>
        <Box
          sx={{
            display: "flex",
            gap: { xs: 2, sm: 3 },
            // Stack vertically on mobile, side-by-side on desktop
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "flex-start",
          }}
        >
          {/* ── Left / Top panel: template grid + color swatches ── */}
          <Box sx={{ width: { xs: "100%", sm: 240 }, flexShrink: 0 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b", letterSpacing: 1, display: "block", mb: 1.5 }}>
              TEMPLATE
            </Typography>
            <Grid container spacing={1} sx={{ mb: 2.5 }}>
              {TEMPLATES.map(({ id, label }, i) => (
                // 4 columns on mobile so all 6 fit in 2 rows; 2 columns on desktop
                <Grid item xs={4} sm={6} key={id}>
                  <Box
                    onClick={() => setTemplateIdx(i)}
                    sx={{
                      p: { xs: 0.75, sm: 1.25 },
                      borderRadius: "8px",
                      cursor: "pointer",
                      textAlign: "center",
                      border: "2px solid",
                      borderColor: templateIdx === i ? palette.hex : "#e2e8f0",
                      bgcolor: templateIdx === i ? palette.hex + "18" : "#fff",
                      transition: "all 0.15s",
                      "&:hover": { borderColor: templateIdx === i ? palette.hex : "#cbd5e1" },
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        display: "block",
                        fontSize: { xs: 9, sm: 11 },
                        lineHeight: 1.3,
                        color: templateIdx === i ? palette.hex : "#0f172a",
                      }}
                    >
                      {label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b", letterSpacing: 1, display: "block", mb: 1.5 }}>
              ACCENT COLOR
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
              {COLORS.map(({ id, hex, label }) => (
                <Box
                  key={id}
                  onClick={() => setColor(id)}
                  title={label}
                  sx={{
                    width: { xs: 32, sm: 28 },
                    height: { xs: 32, sm: 28 },
                    borderRadius: "50%",
                    bgcolor: hex,
                    cursor: "pointer",
                    border: "2.5px solid",
                    borderColor: color === id ? hex : "transparent",
                    outline: color === id ? `3px solid ${hex}` : "none",
                    outlineOffset: "2px",
                    transition: "outline 0.15s",
                  }}
                />
              ))}
            </Box>
            <Typography variant="caption" sx={{ color: "#64748b" }}>
              {palette.label}
            </Typography>
          </Box>

          {/* ── Right / Bottom panel: carousel nav + live preview ── */}
          <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
            {/* Carousel navigation */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1.5,
              }}
            >
              <IconButton
                size="small"
                onClick={prev}
                sx={{ color: "#64748b", bgcolor: "#f1f5f9", "&:hover": { bgcolor: "#e2e8f0" } }}
              >
                <ChevronLeft size={18} />
              </IconButton>
              <Box sx={{ textAlign: "center", flex: 1, px: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a", fontSize: { xs: 13, sm: 14 } }}>
                  {template.label}
                </Typography>
                <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: { xs: 10, sm: 12 } }}>
                  {template.desc}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={next}
                sx={{ color: "#64748b", bgcolor: "#f1f5f9", "&:hover": { bgcolor: "#e2e8f0" } }}
              >
                <ChevronRight size={18} />
              </IconButton>
            </Box>

            {/* Preview frame — scales iframe to fit available width exactly */}
            <Box
              ref={containerRef}
              sx={{
                width: "100%",
                height: containerHeight,
                overflow: "hidden",
                borderRadius: "10px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
                bgcolor: "#fff",
                position: "relative",
              }}
            >
              {previewLoading || !previewHtml ? (
                <Box sx={{ p: 3 }}>
                  <Skeleton variant="text" width="40%" height={32} sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width="60%" height={16} sx={{ mb: 2 }} />
                  <Skeleton variant="rectangular" height={2} sx={{ mb: 2 }} />
                  {[80, 60, 90, 70, 50, 75].map((w, i) => (
                    <Skeleton key={i} variant="text" width={`${w}%`} height={14} sx={{ mb: 0.5 }} />
                  ))}
                </Box>
              ) : (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: IFRAME_W,
                    height: IFRAME_H,
                    transformOrigin: "top left",
                    transform: `scale(${scale})`,
                  }}
                >
                  <iframe
                    key={`${template.id}-${color}`}
                    srcDoc={previewHtml}
                    title="Resume Preview"
                    style={{ width: IFRAME_W, height: IFRAME_H, border: "none", display: "block" }}
                    sandbox="allow-same-origin"
                  />
                </Box>
              )}
            </Box>

            {/* Dot indicators */}
            <Box sx={{ display: "flex", justifyContent: "center", gap: 0.75, mt: 1.5 }}>
              {TEMPLATES.map((_, i) => (
                <Box
                  key={i}
                  onClick={() => setTemplateIdx(i)}
                  sx={{
                    width: templateIdx === i ? 18 : 8,
                    height: 8,
                    borderRadius: "4px",
                    bgcolor: templateIdx === i ? palette.hex : "#e2e8f0",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2.5 }, pt: 1 }}>
        <Button
          onClick={onClose}
          sx={{ color: "#64748b", fontWeight: 600 }}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onDownload(template.id, color)}
          disabled={loading}
          startIcon={
            loading
              ? <CircularProgress size={16} sx={{ color: "#fff" }} />
              : <Download size={16} />
          }
          sx={{
            fontWeight: 700,
            borderRadius: "10px",
            px: { xs: 2, sm: 3 },
            background: `linear-gradient(135deg, ${palette.hex}, ${palette.hex}bb)`,
            "&:hover": { boxShadow: `0 6px 20px ${palette.hex}55` },
            "&:disabled": { opacity: 0.6 },
          }}
        >
          {loading ? "Generating…" : "Download PDF"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
